// 📄 pages/Admin/components/ApiTemplateForm.tsx
// Campos del formulario de una plantilla del Catálogo de APIs Estándar —
// extraído de ApiTemplateCard.tsx para poder reutilizarlo tanto en la
// fila expandible de la lista (ApiTemplateCard.tsx) como en
// NuevaPlantillaModal.tsx (alta de una plantilla nueva). Mismos campos,
// misma lógica que antes — esto es solo una extracción, no un cambio de
// comportamiento.
import { useState } from "react";
import { Plus, ClipboardPaste, Package } from "lucide-react";
import { ApiEndpointConfig } from "../../../services/clienteConexionService.ts";
import { ApiTemplateData } from "../../../services/catalogoApisService.ts";
import { TIPOS_API, PROTOCOLOS_API } from "../../../components/Configuracion/apiTiposConfig.ts";
import { ARIBA_DATA_CENTERS, normalizarAribaDataCenter } from "../../../components/Configuracion/aribaDataCenters.ts";
import EndpointConfigCard from "../../../components/Configuracion/EndpointConfigCard.tsx";
import EndpointCollapsibleItem from "./EndpointCollapsibleItem.tsx";
import ImportarCurlModal from "../../../components/shared/ImportarCurlModal.tsx";
import ImportarColeccionModal from "../../../components/shared/ImportarColeccionModal.tsx";
import { ParsedCurlResult } from "../../../utils/curlParser.ts";

interface ApiTemplateFormProps {
  template: ApiTemplateData;
  onChange: (data: ApiTemplateData) => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const nuevoEndpoint = (): ApiEndpointConfig => ({
  id: generarId(),
  nombre: "",
  endpoint: "",
  metodo: "GET",
  bodyEjemplo: "",
  params: [],
  headers: [],
  pathVariables: []
});

// Etiquetas pedidas para el select ("USA (EE. UU.)" / "Europa (EU)"),
// distintas al label largo de ARIBA_DATA_CENTERS ("Estados Unidos" /
// "Europa") que se usa en el resto de la app.
const DATA_CENTER_LABELS: Record<string, string> = { US: "USA (EE. UU.)", EU: "Europa (EU)" };
const dataCenterLabel = (codigo: string) => DATA_CENTER_LABELS[codigo] || codigo;

export default function ApiTemplateForm({ template, onChange }: ApiTemplateFormProps) {
  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";
  const esAriba = template.tipo === "ariba";
  // authTipo puede venir vacío en plantillas guardadas antes de que este
  // campo existiera — se cae al default de su tipo (mismo criterio que
  // ApiDefinitionForm.tsx).
  const authTipoEfectivo = template.authTipo || TIPOS_API[template.tipo]?.authTipo || "oauth";
  const esOauth = authTipoEfectivo === "oauth";
  const [mostrarImportarCurl, setMostrarImportarCurl] = useState(false);
  const [mostrarImportarColeccion, setMostrarImportarColeccion] = useState(false);

  // Qué endpoints están expandidos — solo UI, no es parte del modelo de
  // datos. Por defecto todos colapsados (ver reglas en el render de
  // abajo: 1 solo endpoint siempre expandido, uno recién agregado o
  // importado por cURL también).
  const [endpointsExpandidos, setEndpointsExpandidos] = useState<Record<string, boolean>>({});
  const toggleEndpointExpandido = (id: string) => setEndpointsExpandidos((prev) => ({ ...prev, [id]: !prev[id] }));

  const update = (field: keyof ApiTemplateData, value: string) => {
    onChange({ ...template, [field]: value });
  };

  // Solo para Ariba: elegir Data Center rellena URL Base / URL del Token
  // ÚNICAMENTE si esos campos están vacíos (cortesía de autocompletado) —
  // nunca pisa texto que el admin ya escribió a mano, sin importar qué
  // Data Center elija después. El resto de la sugerencia (cuando el campo
  // ya tiene algo) se muestra solo como placeholder (ver urlBasePlaceholder
  // / tokenUrlPlaceholder más abajo). "Sin especificar / Otro" (codigo "")
  // no autocompleta nada. Para S/4HANA el Data Center es solo un dato
  // informativo — no hay una URL estándar asociada, así que no toca nada.
  const handleSeleccionarDataCenter = (codigo: string) => {
    if (!esAriba) {
      onChange({ ...template, dataCenter: codigo });
      return;
    }
    const dc = ARIBA_DATA_CENTERS.find((d) => d.codigo === codigo);
    if (!dc) {
      onChange({ ...template, dataCenter: "" });
      return;
    }
    onChange({
      ...template,
      dataCenter: dc.codigo,
      urlBase: template.urlBase.trim() ? template.urlBase : dc.urlBase,
      tokenUrl: template.tokenUrl.trim() ? template.tokenUrl : dc.tokenUrl
    });
  };

  // Placeholder de referencia: mientras el campo esté vacío, muestra la
  // URL del Data Center elegido (o el ejemplo genérico si no hay Data
  // Center) — nunca el valor real del estado, así nunca se pisa lo que
  // el admin escribió.
  const dcSeleccionado = esAriba ? ARIBA_DATA_CENTERS.find((d) => d.codigo === normalizarAribaDataCenter(template.dataCenter)) : undefined;
  const urlBasePlaceholder = dcSeleccionado?.urlBase || (esAriba ? "https://eu.openapi.ariba.com" : "https://myxxxxxx-api.s4hana.cloud.sap");
  const tokenUrlPlaceholder = dcSeleccionado?.tokenUrl || "https://api.ariba.com/v2/oauth/token";

  const addEndpoint = () => {
    const ep = nuevoEndpoint();
    onChange({ ...template, endpoints: [...template.endpoints, ep] });
    setEndpointsExpandidos((prev) => ({ ...prev, [ep.id]: true }));
  };

  const updateEndpoint = (id: string, actualizado: ApiEndpointConfig) => {
    onChange({ ...template, endpoints: template.endpoints.map((ep) => (ep.id === id ? actualizado : ep)) });
  };

  const removeEndpoint = (id: string) => {
    onChange({ ...template, endpoints: template.endpoints.filter((ep) => ep.id !== id) });
    setEndpointsExpandidos((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Rellena la plantilla con lo que el parser de cURL logró inferir (ver
  // components/shared/ImportarCurlModal.tsx / utils/curlParser.ts). Nunca
  // trae credenciales — esas ya vienen descartadas del resultado del
  // parser. Nombre y Protocolo nunca los infiere el parser, quedan para
  // que el admin los complete a mano.
  const handleImportarCurl = (resultado: ParsedCurlResult) => {
    const actualizada: ApiTemplateData = {
      ...template,
      urlBase: resultado.urlBase ?? template.urlBase,
      api: resultado.api ?? template.api,
      dataCenter: resultado.dataCenter ?? template.dataCenter,
      tokenUrl: resultado.tokenUrl ?? template.tokenUrl,
      grantType: resultado.grantType ?? template.grantType
    };

    if (resultado.metodo && resultado.api) {
      const epImportado: ApiEndpointConfig = {
        ...nuevoEndpoint(),
        // endpointSugerido/pathVariablesSugeridas: heurística opcional de
        // detección de IDs hardcodeados en el path (ver curlParser.ts) —
        // solo una sugerencia editable, no un valor definitivo.
        endpoint: resultado.endpointSugerido || "",
        metodo: resultado.metodo,
        bodyEjemplo: resultado.bodyEjemplo || "",
        params: resultado.params || [],
        headers: resultado.headers || [],
        pathVariables: resultado.pathVariablesSugeridas || []
      };
      actualizada.endpoints = [...actualizada.endpoints, epImportado];
      setEndpointsExpandidos((prev) => ({ ...prev, [epImportado.id]: true }));
    }

    onChange(actualizada);
    setMostrarImportarCurl(false);
  };

  // Igual patrón que handleImportarCurl: los endpoints ya vienen armados
  // (ver utils/apiCollectionParser.ts) — solo se agregan a los existentes,
  // nunca los reemplazan. urlBase/api/dataCenter no se tocan acá, el
  // import de colección es puramente de endpoints (ver Paso 3 del modal).
  const handleImportarColeccion = (nuevos: ApiEndpointConfig[]) => {
    const expandidos: Record<string, boolean> = {};
    nuevos.forEach((ep) => { expandidos[ep.id] = true; });
    setEndpointsExpandidos((prev) => ({ ...prev, ...expandidos }));
    onChange({ ...template, endpoints: [...template.endpoints, ...nuevos] });
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between gap-3 mb-1">
          <label className="text-xs font-medium text-gray-400">Nombre de la API</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMostrarImportarCurl(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <ClipboardPaste className="w-3.5 h-3.5" /> Importar desde cURL
            </button>
            <button
              type="button"
              onClick={() => setMostrarImportarColeccion(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <Package className="w-3.5 h-3.5" /> Importar colección (OpenAPI / Postman)
            </button>
          </div>
        </div>
        <input
          type="text"
          className={inputClass}
          value={template.nombre}
          onChange={(e) => update("nombre", e.target.value)}
          placeholder={esAriba ? "Ej: Supplier Data API" : "Ej: S/4HANA Attachment Content"}
        />
      </div>

      {mostrarImportarCurl && (
        <ImportarCurlModal
          modo="catalogo"
          esAriba={esOauth}
          authTipoEsperado={authTipoEfectivo}
          onClose={() => setMostrarImportarCurl(false)}
          onImportar={handleImportarCurl}
        />
      )}

      {mostrarImportarColeccion && (
        <ImportarColeccionModal
          endpointsExistentes={template.endpoints.length}
          onClose={() => setMostrarImportarColeccion(false)}
          onImportar={handleImportarColeccion}
        />
      )}

      <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-4">
        <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">{TIPOS_API[template.tipo]?.label || TIPOS_API.ariba.label}</h4>

        {esAriba && (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                checked={esOauth}
                onChange={() => onChange({ ...template, authTipo: "oauth" })}
              />
              OAuth2 (pide token automático)
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                checked={!esOauth}
                onChange={() => onChange({ ...template, authTipo: "basic" })}
              />
              Manual / Básica (sin token)
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>URL Base</label>
            <input
              type="text"
              className={inputClass}
              value={template.urlBase}
              onChange={(e) => update("urlBase", e.target.value)}
              placeholder={urlBasePlaceholder}
            />
            {esAriba && !!template.dataCenter && (
              <p className="text-[11px] text-gray-500 mt-1">Sugerencia del Data Center mostrada como placeholder — tu texto nunca se sobreescribe.</p>
            )}
          </div>
          <div>
            <label className={labelClass}>API Path</label>
            <input
              type="text"
              className={inputClass}
              value={template.api}
              onChange={(e) => update("api", e.target.value)}
              placeholder={esAriba ? "/api/supplierdatapagination/v4/prod" : "/sap/opu/odata/sap/API_CV_ATTACHMENT_SRV"}
            />
          </div>
          <div>
            <label className={labelClass}>Protocolo</label>
            <select className={inputClass} value={template.protocolo} onChange={(e) => update("protocolo", e.target.value)}>
              <option value="">Sin especificar</option>
              {PROTOCOLOS_API.map((protocolo) => (
                <option key={protocolo} value={protocolo}>{protocolo}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Data Center</label>
            <select
              className={inputClass}
              value={esAriba ? normalizarAribaDataCenter(template.dataCenter) : template.dataCenter}
              onChange={(e) => handleSeleccionarDataCenter(e.target.value)}
            >
              <option value="">Sin especificar / Otro</option>
              {ARIBA_DATA_CENTERS.filter((dc) => dc.disponible).map((dc) => (
                <option key={dc.codigo} value={dc.codigo}>{dataCenterLabel(dc.codigo)}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-[11px] text-gray-500">URL Base y API Path se piden una sola vez — todos los endpoints de esta plantilla las comparten.</p>
      </div>

      {esOauth && (
        <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
          <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Token (si aplica)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>URL del Token</label>
              <input
                type="text"
                className={inputClass}
                value={template.tokenUrl}
                onChange={(e) => update("tokenUrl", e.target.value)}
                placeholder={tokenUrlPlaceholder}
              />
            </div>
            <div>
              <label className={labelClass}>Grant Type</label>
              <select className={inputClass} value={template.grantType} onChange={(e) => update("grantType", e.target.value)}>
                <option value="">Automático (openapi_2lo)</option>
                <option value="openapi_2lo">openapi_2lo</option>
                <option value="client_credentials">client_credentials</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Endpoints</h4>
          <button
            type="button"
            onClick={addEndpoint}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar endpoint
          </button>
        </div>

        {template.endpoints.length === 0 ? (
          <p className="text-xs text-gray-500 bg-black/20 border border-white/10 rounded-lg p-3">
            Todavía no hay endpoints en esta plantilla.
          </p>
        ) : template.endpoints.length === 1 ? (
          // Un solo endpoint: no vale la pena colapsarlo, se muestra
          // siempre expandido tal como antes.
          <EndpointConfigCard
            endpoint={template.endpoints[0]}
            urlBase={template.urlBase}
            api={template.api}
            readOnly={false}
            onChange={(actualizado) => updateEndpoint(template.endpoints[0].id, actualizado)}
            onRemove={() => removeEndpoint(template.endpoints[0].id)}
          />
        ) : (
          <div className="space-y-3">
            {template.endpoints.map((ep) => (
              <EndpointCollapsibleItem
                key={ep.id}
                endpoint={ep}
                urlBase={template.urlBase}
                api={template.api}
                expandido={!!endpointsExpandidos[ep.id]}
                onToggle={() => toggleEndpointExpandido(ep.id)}
                onChange={(actualizado) => updateEndpoint(ep.id, actualizado)}
                onRemove={() => removeEndpoint(ep.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
